import moment from 'moment';
import { Space, Tag, Tooltip, Typography } from 'antd';
import { StopFilled } from '@ant-design/icons';
import { ColumnsType, ColumnType } from 'antd/lib/table';
import TooltipWithCopy from 'components/ui/composed/tooltipWithCopy/TooltipWithCopy';
import DeclarationStatusTag from 'components/ui/composed/declarations/declaration-status/DeclarationStatusTag';
import EmptyCell from 'components/new-ui/base/emtpy-cell/EmptyCell';
import { hasPermissions } from 'config/roles/hasPermissions';
import { Declaration } from 'store/declarations/declaration';
import { DeclarationStatuses } from 'store/declarations/enums/common/declaration-status';
import {
    getIcs2MessageTypeLabel,
    Ics2TableRow,
    isIcs2GroupRow,
} from 'store/declarations/eu/ics2/ics2-declaration-list';
import { User } from 'store/session/user';
import { ActionEvents } from './DeclarationColumns';
import Actions from './components/column-components/Actions';
import GroupActions from './components/column-components/GroupActions';
import Mrn from './components/column-components/Mrn';
import { ReactElement, ReactNode } from 'react';

const DATE_FORMAT = 'DD/MM/YY, HH:mm A';
const TEST_ID_PREFIX = 'ens-declarations-table';

const dataColumn = ({
    key,
    title,
    testId,
    className,
    render,
}: {
    key: string;
    title: string;
    testId: string;
    className?: string;
    render: (record: Ics2TableRow, dataTestId: string) => ReactNode;
}): ColumnType<Ics2TableRow> => ({
    key,
    title,
    dataIndex: key,
    className,
    render: (_: unknown, record: Ics2TableRow) => {
        const dataTestId = `${TEST_ID_PREFIX}-${testId}-row`;
        return render(record, dataTestId) || <EmptyCell dataTestId={dataTestId} />;
    },
});

const dataColumns: ColumnsType<Ics2TableRow> = [
    {
        // Tree data renders the expand icon into the cell at `expandIconColumnIndex` (0 by default),
        // so this column exists to hold the chevron on its own.
        key: 'expand',
        width: 1,
        render: () => null,
    },
    {
        key: 'notifications',
        width: 1,
        align: 'center',
        render: (_: unknown, record: Ics2TableRow) => {
            const notificationIcons: ReactElement[] = [];

            if (record.doNotLoadReceived) {
                notificationIcons.push(
                    <Tooltip title="Do Not Load (DNL) order received">
                        <StopFilled
                            data-testid={`${TEST_ID_PREFIX}-dnl-row`}
                            style={{ color: 'var(--error-normal)' }}
                        />
                    </Tooltip>
                );
            }

            return <Space size={8}>{notificationIcons}</Space>;
        },
    },
    dataColumn({
        key: 'referenceId',
        title: 'Reference ID',
        testId: 'reference',
        className: 'ics2-reference-cell',
        render: ({ referenceId }, dataTestId) =>
            referenceId && (
                <TooltipWithCopy copyText={referenceId} dataTestId={dataTestId}>
                    {referenceId}
                </TooltipWithCopy>
            ),
    }),
    dataColumn({
        key: 'messageType',
        title: 'Type',
        testId: 'type',
        render: ({ messageType }, dataTestId) => {
            const label = getIcs2MessageTypeLabel(messageType);
            return label && <Typography.Text data-testid={dataTestId}>{label}</Typography.Text>;
        },
    }),
    dataColumn({
        key: 'mrn',
        title: 'MRN',
        testId: 'mrn',
        render: ({ mrn }, dataTestId) => <Mrn mrn={mrn ?? undefined} dataTestId={dataTestId} />,
    }),
    dataColumn({
        key: 'consignor',
        title: 'Consignor',
        testId: 'consignor',
        render: ({ consignor }, dataTestId) =>
            consignor && <Typography.Text data-testid={dataTestId}>{consignor}</Typography.Text>,
    }),
    dataColumn({
        key: 'consignee',
        title: 'Consignee',
        testId: 'consignee',
        render: ({ consignee }, dataTestId) =>
            consignee && <Typography.Text data-testid={dataTestId}>{consignee}</Typography.Text>,
    }),
    dataColumn({
        key: 'teamName',
        title: 'Team',
        testId: 'team',
        render: ({ teamName }, dataTestId) =>
            teamName && (
                <Tag color="success" data-testid={dataTestId}>
                    {teamName}
                </Tag>
            ),
    }),
    dataColumn({
        key: 'createdAt',
        title: 'Created',
        testId: 'createdAt',
        render: ({ createdByName, createdAt }, dataTestId) =>
            (createdByName || createdAt) && (
                <div data-testid={dataTestId}>
                    {createdByName && <div>{createdByName}</div>}
                    {createdAt && <div>{moment(createdAt).format(DATE_FORMAT)}</div>}
                </div>
            ),
    }),
    dataColumn({
        key: 'status',
        title: 'Status',
        testId: 'status',
        render: (record, dataTestId) => {
            if (isIcs2GroupRow(record)) return <Tag data-testid={dataTestId}>Group</Tag>;

            const status: DeclarationStatuses | null = record.status;
            return status && <DeclarationStatusTag status={status} dataTestId={dataTestId} />;
        },
    }),
];

/**
 * The listing rows are summaries, not full declarations — the action buttons only read `id`,
 * `status` and `archived`, so a declaration of that shape is enough to drive them.
 */
const toActionsRecord = (record: Ics2TableRow) =>
    ({ id: record.id, status: record.status ?? undefined, archived: record.archived } as Declaration);

const getIcs2ActionsColumn = (actions: ActionEvents, user: User | undefined): ColumnsType<Ics2TableRow> => [
    {
        title: 'Actions',
        key: 'actions',
        width: 60,
        onCell: () => ({
            onClick: (e: React.MouseEvent) => {
                e.stopPropagation();
            },
        }),
        render: (_: unknown, record: Ics2TableRow) => {
            const showActions = hasPermissions(user, 'declarations', 'UPDATE');
            const canArchive = hasPermissions(user, 'declarations', 'ARCHIVE');
            const canDelete = hasPermissions(user, 'declarations', 'DELETE');

            return isIcs2GroupRow(record) ? (
                <GroupActions
                    showActions={showActions}
                    sourceManifestId={record.sourceManifestId ?? record.id}
                    archived={record.archived}
                    actions={actions.groupActions}
                />
            ) : (
                <Actions
                    showActions={showActions}
                    canArchive={canArchive}
                    canDelete={canDelete}
                    declaration={toActionsRecord(record)}
                    actions={actions}
                />
            );
        },
    },
];

type Ics2ColumnOptions = {
    hasGroups?: boolean;
    hasNotifications?: boolean;
};

export const getIcs2Columns = (
    actions: ActionEvents,
    user: User | undefined,
    options?: Ics2ColumnOptions
): ColumnsType<Ics2TableRow> => {
    const { filterDataColumns, ...actionButtons } = actions;

    const columns = dataColumns.filter(
        (column) =>
            (column.key !== 'expand' || options?.hasGroups !== false) &&
            (column.key !== 'notifications' || options?.hasNotifications !== false)
    );

    const _dataColumns = !!filterDataColumns ? filterDataColumns(columns) : columns;

    return Object.values(actionButtons).some((action) => action)
        ? [..._dataColumns, ...getIcs2ActionsColumn(actionButtons, user)]
        : [..._dataColumns];
};
